<template>
  <el-card>
    <div slot="header" class="toolbar">
      <span>商品分类信息</span>
      <div>
        <el-button v-if="$hasPermission('mall:category:save')" type="primary" size="mini" @click="openCreate">新增分类</el-button>
      </div>
    </div>

    <div class="search-bar search-wrap">
      <el-input
        v-model.trim="query.categoryName"
        placeholder="请输入分类名称"
        clearable
        style="width: 260px"
        @keyup.enter.native="handleSearch"
      />
      <el-button type="primary" @click="handleSearch">查询</el-button>
      <el-button @click="reset">重置</el-button>
    </div>

    <el-table :data="list" border style="width: 100%">
      <el-table-column prop="id" label="编号" width="90" />
      <el-table-column prop="categoryName" label="分类名称" min-width="180" />
      <el-table-column prop="parentName" label="上级分类" width="160">
        <template slot-scope="{ row }">
          {{ row.parentName || '顶级分类' }}
        </template>
      </el-table-column>
      <el-table-column label="分类图标" width="120">
        <template slot-scope="{ row }">
          <el-image
            v-if="row.iconUrl"
            :src="resolveFileUrl(row.iconUrl)"
            :preview-src-list="[resolveFileUrl(row.iconUrl)]"
            fit="cover"
            class="table-image"
          >
            <div slot="error" class="table-image-error">无图</div>
          </el-image>
          <div v-else class="table-image-error table-image-empty">无图</div>
        </template>
      </el-table-column>
      <el-table-column prop="iconUrl" label="图标地址" min-width="240" show-overflow-tooltip />
      <el-table-column prop="sortNum" label="排序" width="90" />
      <el-table-column prop="status" label="状态" width="90">
        <template slot-scope="{ row }">
          {{ row.status === 1 ? '启用' : '禁用' }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="260" fixed="right">
        <template slot-scope="{ row }">
          <el-button v-if="$hasPermission('mall:category:update')" size="mini" @click="openEdit(row)">编辑</el-button>
          <el-button
            v-if="$hasPermission('mall:category:update')"
            :type="row.status === 1 ? 'warning' : 'success'"
            size="mini"
            @click="toggleStatus(row)"
          >
            {{ row.status === 1 ? '禁用' : '启用' }}
          </el-button>
          <el-tooltip
            v-if="$hasPermission('mall:category:delete')"
            :disabled="canDelete(row)"
            :content="deleteDisabledReason(row)"
            placement="top"
          >
            <span class="action-tooltip-wrap">
              <el-button
                size="mini"
                type="danger"
                :disabled="!canDelete(row)"
                @click="remove(row)"
              >
                删除
              </el-button>
            </span>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-bar">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="query.page"
        :page-size="query.limit"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
      />
    </div>

    <el-dialog :title="form.id ? '编辑分类' : '新增分类'" :visible.sync="dialogVisible" width="640px" :close-on-click-modal="false">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="上级分类">
          <el-select
            v-model.number="form.parentId"
            :disabled="parentSelectDisabled"
            style="width: 100%"
            placeholder="请选择上级分类"
          >
            <el-option :value="0" label="顶级分类" />
            <el-option
              v-for="item in selectableParentOptions"
              :key="item.id"
              :label="item.categoryName"
              :value="item.id"
            />
          </el-select>
          <div v-if="parentSelectDisabled" class="form-tip">当前一级分类下已有子分类，不能改挂到其它一级分类下面。</div>
        </el-form-item>
        <el-form-item label="分类名称" prop="categoryName">
          <el-input
            v-model.trim="form.categoryName"
            maxlength="10"
            show-word-limit
            placeholder="分类名称最多10个字"
          />
        </el-form-item>
        <el-form-item label="分类图标" prop="iconUrl">
          <image-selector
            v-model="form.iconUrl"
            :category-code.sync="form.iconCategoryCode"
            :allowed-categories="['category-icon']"
          />
        </el-form-item>
        <el-form-item label="排序">
          <el-input v-model.number="form.sortNum" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button
          v-if="$hasPermission(form.id ? 'mall:category:update' : 'mall:category:save')"
          type="primary"
          @click="save"
        >
          保存
        </el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import http from '../../utils/http'
import { resolveFileUrl } from '../../utils/file'
import ImageSelector from '../../components/mall/ImageSelector.vue'

function defaultForm () {
  return {
    id: null,
    parentId: 0,
    categoryName: '',
    iconUrl: '',
    iconCategoryCode: 'category-icon',
    sortNum: 0,
    status: 1,
    childCount: 0
  }
}

function defaultQuery () {
  return {
    page: 1,
    limit: 10,
    categoryName: ''
  }
}

export default {
  components: {
    ImageSelector
  },
  data () {
    return {
      query: defaultQuery(),
      total: 0,
      list: [],
      allCategoryOptions: [],
      dialogVisible: false,
      form: defaultForm(),
      rules: {
        categoryName: [{ required: true, message: '请输入分类名称', trigger: 'blur' }],
        iconUrl: [{ required: true, message: '请选择分类图标', trigger: 'change' }]
      }
    }
  },
  computed: {
    selectableParentOptions () {
      return this.allCategoryOptions.filter(item => {
        const itemId = Number(item.id)
        const parentId = Number(item.parentId || 0)
        return itemId !== Number(this.form.id || 0) && parentId === 0
      })
    },
    parentSelectDisabled () {
      return Boolean(this.form.id) && Number(this.form.parentId || 0) === 0 && Number(this.form.childCount || 0) > 0
    }
  },
  created () {
    this.load()
    this.loadAllCategoryOptions()
  },
  methods: {
    resolveFileUrl,
    async load () {
      const { data } = await http.get('/mall/category/list', { params: this.query })
      const pageData = data.data || {}
      this.list = pageData.list || []
      this.total = Number(pageData.total || 0)
    },
    async loadAllCategoryOptions () {
      const { data } = await http.get('/mall/category/options')
      this.allCategoryOptions = (data.data || []).map(item => ({
        ...item,
        id: Number(item.id),
        parentId: Number(item.parentId || 0)
      }))
    },
    handleSearch () {
      this.query.page = 1
      this.load()
    },
    handlePageChange (page) {
      this.query.page = page
      this.load()
    },
    handleSizeChange (limit) {
      this.query.limit = limit
      this.query.page = 1
      this.load()
    },
    reset () {
      this.query = defaultQuery()
      this.load()
    },
    openCreate () {
      this.form = defaultForm()
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate()
      })
    },
    async openEdit (row) {
      const { data } = await http.get(`/mall/category/info/${row.id}`)
      const info = data.data || {}
      this.form = {
        id: info.id,
        parentId: Number(info.parentId || 0),
        categoryName: info.categoryName || '',
        iconUrl: info.iconUrl || '',
        iconCategoryCode: 'category-icon',
        sortNum: info.sortNum || 0,
        status: info.status,
        childCount: Number(row.childCount || 0)
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate()
      })
    },
    toggleStatus (row) {
      const nextStatus = row.status === 1 ? 0 : 1
      const actionText = nextStatus === 1 ? '启用' : '禁用'
      const message = nextStatus === 0
        ? `确定禁用分类“${row.categoryName}”吗？禁用后会同步禁用下级分类、分类下商品，以及这些商品在热门推荐信息中的推荐，小程序将不再展示这些商品。`
        : `确定启用分类“${row.categoryName}”吗？启用分类只恢复分类状态，不会自动上架商品或恢复热门推荐。`
      this.$confirm(message, '提示', {
        type: 'warning',
        confirmButtonText: actionText,
        cancelButtonText: '取消'
      }).then(async () => {
        const { data } = await http.post('/mall/category/status', {
          id: row.id,
          status: nextStatus
        })
        if (data.code === 0) {
          this.$message.success(nextStatus === 0 ? '分类及关联展示已同步禁用' : '分类启用成功')
          await this.loadAllCategoryOptions()
          this.load()
        }
      })
    },
    save () {
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        if ((this.form.categoryName || '').length > 10) {
          this.$message.warning('分类名称过长,请调整后提交')
          return
        }
        const payload = {
          id: this.form.id,
          parentId: Number(this.form.parentId || 0),
          categoryName: this.form.categoryName,
          iconUrl: this.form.iconUrl,
          sortNum: this.form.sortNum,
          status: this.form.status
        }
        const url = this.form.id ? '/mall/category/update' : '/mall/category/save'
        const { data } = await http.post(url, payload)
        if (data.code === 0) {
          this.$message.success(this.form.id ? '分类修改成功' : '分类新增成功')
          this.dialogVisible = false
          await this.loadAllCategoryOptions()
          this.load()
        }
      })
    },
    remove (row) {
      if (!this.canDelete(row)) {
        this.$message.warning(this.deleteDisabledReason(row))
        return
      }
      this.$confirm(`确定删除分类“${row.categoryName}”吗？`, '提示', { type: 'warning' }).then(async () => {
        const { data } = await http.post('/mall/category/delete', [row.id])
        if (data.code === 0) {
          this.$message.success('分类删除成功')
          await this.loadAllCategoryOptions()
          this.load()
        }
      })
    },
    canDelete (row) {
      if (row && row.canDelete !== undefined) {
        return Boolean(row.canDelete)
      }
      return Number(row.childCount || 0) === 0 && Number(row.goodsCount || 0) === 0
    },
    deleteDisabledReason (row) {
      if (row && row.deleteDisabledReason) {
        return row.deleteDisabledReason
      }
      if (Number((row && row.childCount) || 0) > 0) {
        return '该分类下还有子分类，请先处理子分类'
      }
      if (Number((row && row.goodsCount) || 0) > 0) {
        return '该分类下还有商品，请先删除或迁移商品'
      }
      return ''
    }
  }
}
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.search-wrap {
  flex-wrap: wrap;
}

.table-image {
  width: 64px;
  height: 64px;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  cursor: pointer;
}

.table-image-error {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.table-image-empty {
  font-size: 12px;
}

.pagination-bar {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.form-tip {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}

.action-tooltip-wrap {
  display: inline-block;
}
</style>
